<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="page">
	@let canSeeTotalEmission = authorizationService.canSee(grants.totalEmission);
	@let canSeeScope2Emission = authorizationService.canSee(grants.scope2Emission);
	@let canSeeScope3Emission = authorizationService.canSee(grants.scope3Emission);
	@let canSeeTotalAssets = authorizationService.canSee(grants.totalAssetsCount);
	@let canSeeManufacturerCount = authorizationService.canSee(grants.manufacturerCount);
	@let canSeeEmissionsOverTime = authorizationService.canSee(grants.totalCo2OverTime);
	@let canSeeManufacturerEmission =
		authorizationService.canSee(grants.totalEmissionByManufacturer);
	@let canSeeCarbonFootprint =
		authorizationService.canSee(grants.carbonFootprintByDeviceCategory);
	@let canSeeScopePercentage =
		authorizationService.canSee(grants.carbonFootprintScopeBreakdownPercentageView);
	@let canSeeScopeValue =
		authorizationService.canSee(grants.carbonFootprintScopeBreakdownTrueValueView);
	@let canSeeModelImpact = authorizationService.canSee(grants.operationalCo2ByModel);
	@let canSeeRegionImpact = authorizationService.canSee(grants.topCountriesByTotalCo2Emissions);
	@let canSeeInstallStatus = authorizationService.canSee(grants.operationalCo2ByDeviceStatus);
	@let canSeeLifecycle =
		authorizationService.canSee(grants.emissionsReductionAchievedByLifecycleExtension);
	@let canSeeCountryOverTime = authorizationService.canSee(grants.operationalCo2ByCountry);
	@let canSeeAnyWidget =
		canSeeTotalEmission ||
		canSeeScope2Emission ||
		canSeeScope3Emission ||
		canSeeTotalAssets ||
		canSeeManufacturerCount ||
		canSeeEmissionsOverTime ||
		canSeeManufacturerEmission ||
		canSeeCarbonFootprint ||
		canSeeScopePercentage ||
		canSeeScopeValue ||
		canSeeModelImpact ||
		canSeeRegionImpact ||
		canSeeInstallStatus ||
		canSeeLifecycle ||
		canSeeCountryOverTime;
	@let canUpload = authorizationService.hasPermission(permissions.HARDWARE_ASSETS_UPLOAD);
	@let canRename = authorizationService.hasPermission(permissions.HARDWARE_ASSETS_RENAME);
	@let canDelete = authorizationService.hasPermission(permissions.HARDWARE_ASSETS_DELETE);

	@if (viewMode === "baseline") {
		<div class="view-switch">
			<globalictcc-switch
				[options]="viewSwitchOptions"
				(handleSwitch)="onViewSwitch($event)"
			/>

			@if (isServiceNowEnabled && canSeeAnyWidget) {
				<div class="period">
					<globalictcc-dropdown
						label="Reporting period"
						[formControl]="periodControl"
						[options]="periodOptions"
					/>

					<globalictcc-button
						label="Apply"
						[disabled]="!periodControl.value"
						(handleClick)="onApply()"
					/>
				</div>
			}
		</div>
	}
	<div class="dashboard-loading-region">
		@if (hasError) {
			<div class="error">
				<div class="error-message">
					<span class="icon-unlink"></span>

					<span class="message">
						There are some errors in the file you uploaded, please upload the file in
						correct form.
					</span>

					<button
						class="error-log-toggle"
						(click)="isErrorLogOpened = !isErrorLogOpened"
					>
						Error log

						<span
							class="icon-chevron-down"
							[ngClass]="{ 'is-opened': isErrorLogOpened }"
						></span>
					</button>

					@if (isErrorLogOpened) {
						<span class="error-log">
							{{ errorLogMessage }}
						</span>
					}

					@if (canUpload) {
						<globalictcc-button
							label="Retry"
							iconName="update"
							[textOnly]="true"
							(handleClick)="hasConfirmed = false"
						/>
					}
				</div>
			</div>
		} @else if (hasConfirmed || isServiceNowEnabled) {
			<div class="top-actions">
				@if (!isServiceNowEnabled) {
					<div class="uploaded-file-info">
						<globalictcc-dropdown
							[isHuge]="true"
							[options]="filesOptions"
							[formControl]="fileControl"
							(handleSelectionChange)="onFileChange($event)"
						/>

						@if (canRename || canDelete) {
							<div class="actions">
								@if (canRename) {
									<globalictcc-button
										label="Rename"
										iconName="edit"
										[textOnly]="true"
										(handleClick)="onRename()"
									/>
								}
								@if (canDelete) {
									<globalictcc-button
										label="Delete"
										iconName="delete"
										[textOnly]="true"
										(handleClick)="onDelete()"
									/>
								}
							</div>
						}
					</div>
					@if (canUpload) {
						<globalictcc-button
							label="Upload file"
							(handleClick)="onUploadDeviceList()"
						/>
					}
				}
			</div>

			@if (
				canSeeTotalEmission ||
				canSeeScope2Emission ||
				canSeeScope3Emission ||
				canSeeTotalAssets ||
				canSeeManufacturerCount
			) {
				@let generalInformation = generalInformation$ | async;
				<div class="kpis">
					@if (canSeeTotalEmission) {
						<globalictcc-kpi
							[label]="generalInformation?.data?.totalEmission?.label"
							[value]="generalInformation?.data?.totalEmission?.value ?? 0"
							[unit]="generalInformation?.data?.totalEmission?.unit"
						/>
					}
					@if (canSeeScope2Emission) {
						<globalictcc-kpi
							[label]="generalInformation?.data?.scope2Emission?.label"
							[value]="generalInformation?.data?.scope2Emission?.value ?? 0"
							[unit]="generalInformation?.data?.scope2Emission?.unit"
						/>
					}
					@if (canSeeScope3Emission) {
						<globalictcc-kpi
							[label]="generalInformation?.data?.scope3Emission?.label"
							[value]="generalInformation?.data?.scope3Emission?.value ?? 0"
							[unit]="generalInformation?.data?.scope3Emission?.unit"
						/>
					}
					@if (canSeeTotalAssets) {
						<globalictcc-kpi
							[label]="generalInformation?.data?.totalAssetsCount?.label"
							[value]="generalInformation?.data?.totalAssetsCount?.value ?? 0"
							[unit]="generalInformation?.data?.totalAssetsCount?.unit"
						/>
					}
					@if (isServiceNowEnabled && canSeeTotalAssets) {
						<globalictcc-kpi
							[label]="
								generalInformation?.data?.accountedAssetsInReportingPeriod?.label
							"
							[value]="
								generalInformation?.data?.accountedAssetsInReportingPeriod?.value ??
								0
							"
							[unit]="
								generalInformation?.data?.accountedAssetsInReportingPeriod?.unit
							"
						/>
					}
					@if (canSeeManufacturerCount) {
						<globalictcc-kpi
							[label]="generalInformation?.data?.manufacturersCount?.label"
							[value]="generalInformation?.data?.manufacturersCount?.value ?? 0"
							[unit]="generalInformation?.data?.manufacturersCount?.unit"
						/>
					}
				</div>
			}

			<!-- Emissions over time -->
			@if (canSeeEmissionsOverTime) {
				@let emissionsOverTime = emissionsOverTime$ | async;
				<section class="chart">
					<div class="section-title">
						<span class="chart-title">
							{{ emissionsOverTime?.data?.chart?.title }}
							<span class="icon-warning"></span>
						</span>

						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(
									emissionsOverTimeLineChart.instance,
									'emissions_over_time'
								)
							"
						/>
					</div>

					<div class="table-chart">
						<globalictcc-chart
							#emissionsOverTimeLineChart
							chartType="line"
							[data]="emissionsOverTime?.data?.chart"
							[isLoading]="emissionsOverTime?.isLoading ?? true"
							[hasError]="
								emissionsOverTime?.hasError || emissionsOverTime?.data?.hasError
							"
							[isLegendVisible]="true"
							[isTitleVisible]="false"
						/>
					</div>
				</section>
			}

			@if (canSeeManufacturerEmission) {
				@let manufacturerWidget = totalEmissionByManufacturer$ | async;
				@let categoryOptions = categories$ | async;
				<section class="chart">
					<div class="section-title">
						<span class="chart-title">
							Total emission by manufacturer <span class="icon-warning"></span>
						</span>

						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(totalEmissionChart.instance, 'total_emission')
							"
						/>
					</div>

					<div class="filter">
						<globalictcc-dropdown
							label="Category"
							[options]="categoryOptions?.data ?? []"
							[multi]="true"
							[formControl]="categoriesControl"
						/>

						<globalictcc-button
							label="Apply"
							(handleClick)="fetchTotalEmissions()"
						/>
					</div>

					<div class="table-chart">
						<div class="table">
							<globalictcc-table
								[gridOptions]="totalEmissionGridOptions"
								[hasError]="
									manufacturerWidget?.hasError ||
									manufacturerWidget?.data?.[1]?.hasError ||
									totalEmissionTableHasError
								"
								(handleTableRefresh)="fetchManufacturesEmission()"
							/>
						</div>

						<globalictcc-chart
							#totalEmissionChart
							chartType="pie"
							height="471px"
							labelPosition="outside"
							[isTitleVisible]="false"
							[data]="manufacturerWidget?.data?.[0]?.chart"
							[hasError]="
								manufacturerWidget?.hasError ||
								manufacturerWidget?.data?.[0]?.hasError
							"
							[isLoading]="manufacturerWidget?.isLoading ?? true"
							[colors]="totalEmissionPieChartColors"
							[configOverrides]="pieChartOverrides()"
							(handleChartRefresh)="fetchTotalEmissionPieChart()"
						/>
					</div>
				</section>
			}
			@if (canSeeCarbonFootprint) {
				@let carbonFootprint = carbonFootprint$ | async;
				<section class="chart">
					<div class="section-title">
						<span class="chart-title">
							Carbon footprint by device category <span class="icon-warning"></span>
						</span>

						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(carbonFootprintChart.instance, 'carbon_footprint')
							"
						/>
					</div>

					<globalictcc-chart
						#carbonFootprintChart
						chartType="barX"
						[isTitleVisible]="false"
						[isLegendVisible]="true"
						[data]="carbonFootprint?.data?.chart"
						[hasError]="carbonFootprint?.hasError || carbonFootprint?.data?.hasError"
						[isLoading]="carbonFootprint?.isLoading ?? true"
						[colors]="carbonFootprintBarChartColors"
						[configOverrides]="carbonFootprintBarChartOverrides"
						(handleChartRefresh)="fetchCarbonFootprint()"
					/>
				</section>
			}

			@if (canSeeScopePercentage || canSeeScopeValue) {
				@let manufacturers = manufacturers$ | async;
				<section class="chart">
					<div class="section-title">
						<span class="chart-title">
							Carbon footprint scope breakdown <span class="icon-warning"></span>
						</span>
						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(scopeChart?.instance, 'carbon_footprint_by_scope')
							"
						/>
					</div>
					<div class="table-chart">
						<div class="table-with-apply">
							<div class="table">
								<globalictcc-table
									[gridOptions]="manufacturersGridOptions"
									[hasError]="
										manufacturers?.hasError ||
										manufacturers?.data?.hasError ||
										manufacturersHasError
									"
									(handleTableRefresh)="fetchManufacturers()"
								/>
							</div>
							<globalictcc-button
								label="Apply"
								(handleClick)="fetchCarbonFootprintByScope()"
							/>
						</div>
						<div class="third-section-chart">
							<div class="third-section-actions">
								@if (canSeeScopePercentage && canSeeScopeValue) {
									<globalictcc-chips
										[chips]="chips"
										(handleSelection)="onModeChange($event)"
									/>
								}
								<globalictcc-dropdown
									label="Order by"
									[options]="orderByOptions"
									[formControl]="orderByControl"
									(handleSelectionChange)="onOrderByChange($event)"
								/>
							</div>
							@if (canSeeScopePercentage && canSeeScopeValue) {
								@let scope = carbonFootprintByScope$ | async;
								<globalictcc-chart
									#scopeChart
									chartType="barY"
									[isTitleVisible]="false"
									[isLegendVisible]="true"
									labelPosition="outside"
									[data]="scope?.data?.chart"
									[hasError]="scope?.hasError || scope?.data?.hasError"
									[isLoading]="scope?.isLoading ?? true"
									[colors]="carbonFootprintBarChartColors"
									[configOverrides]="carbonFootprintByScopeBarChartOverrides"
									(handleChartRefresh)="fetchCarbonFootprintByScope()"
								/>
							} @else if (canSeeScopePercentage) {
								@let scope = carbonFootprintByScopePercentage$ | async;
								<globalictcc-chart
									#scopeChart
									chartType="barY"
									[isTitleVisible]="false"
									[isLegendVisible]="true"
									labelPosition="outside"
									[data]="scope?.data?.chart"
									[hasError]="scope?.hasError || scope?.data?.hasError"
									[isLoading]="scope?.isLoading ?? true"
									[colors]="carbonFootprintBarChartColors"
									[configOverrides]="carbonFootprintByScopeBarChartOverrides"
									(handleChartRefresh)="fetchCarbonFootprintByScope()"
								/>
							} @else {
								@let scope = carbonFootprintByScopeValue$ | async;
								<globalictcc-chart
									#scopeChart
									chartType="barY"
									[isTitleVisible]="false"
									[isLegendVisible]="true"
									labelPosition="outside"
									[data]="scope?.data?.chart"
									[hasError]="scope?.hasError || scope?.data?.hasError"
									[isLoading]="scope?.isLoading ?? true"
									[colors]="carbonFootprintBarChartColors"
									[configOverrides]="carbonFootprintByScopeBarChartOverrides"
									(handleChartRefresh)="fetchCarbonFootprintByScope()"
								/>
							}
						</div>
					</div>
				</section>
			}

			<!-- Highest environmental impact - Models -->
			@if (canSeeModelImpact) {
				@let modelImpact = modelImpact$ | async;
				@defer (on viewport; prefetch on idle) {
					<section class="chart models-impact">
						<div class="section-title">
							<span class="chart-title">
								{{
									modelImpact?.data?.[0]?.chart?.title ||
										modelImpact?.data?.[1]?.chart?.title
								}}
								<span class="icon-warning"></span>
							</span>

							<div class="section-actions">
								<globalictcc-button
									label="Export total"
									[textOnly]="true"
									iconName="download"
									[disabled]="
										!!modelImpact?.hasError ||
										!!modelImpact?.data?.[0]?.hasError
									"
									(handleClick)="
										onExportChart(
											worstModelsBarChart.instance,
											'top_10_models_by_total_co2e'
										)
									"
								/>
								<globalictcc-button
									label="Export per device"
									[textOnly]="true"
									iconName="download"
									[disabled]="
										!!modelImpact?.hasError ||
										!!modelImpact?.data?.[1]?.hasError
									"
									(handleClick)="
										onExportChart(
											worstModelsPerDeviceBarChart.instance,
											'top_10_models_by_avg_co2e'
										)
									"
								/>
							</div>
						</div>

						<div class="chart-grid">
							<div class="chart-wrapper">
								<span class="chart-label">Total</span>
								<globalictcc-chart
									#worstModelsBarChart
									chartType="barY"
									[data]="modelImpact?.data?.[0]?.chart"
									[isLoading]="modelImpact?.isLoading ?? true"
									[hasError]="
										modelImpact?.hasError || modelImpact?.data?.[0]?.hasError
									"
									[isGradient]="true"
									[isTitleVisible]="false"
								/>
							</div>
							<div class="chart-wrapper">
								<span class="chart-label">Per device</span>
								<globalictcc-chart
									#worstModelsPerDeviceBarChart
									chartType="barY"
									[data]="modelImpact?.data?.[1]?.chart"
									[isLoading]="modelImpact?.isLoading ?? true"
									[hasError]="
										modelImpact?.hasError || modelImpact?.data?.[1]?.hasError
									"
									[isGradient]="true"
									[isTitleVisible]="false"
								/>
							</div>
						</div>
					</section>
				} @placeholder {
					<div></div>
				}
			}

			<!-- Highest environmental impact - Regions -->
			@if (canSeeRegionImpact) {
				@let regionImpact = regionImpact$ | async;
				@defer (on viewport; prefetch on idle) {
					<section class="chart regions-impact">
						<div class="section-title">
							<span class="chart-title">
								{{
									regionImpact?.data?.[0]?.chart?.title ||
										regionImpact?.data?.[1]?.chart?.title
								}}
								<span class="icon-warning"></span>
							</span>

							<div class="section-actions">
								<globalictcc-button
									label="Export total"
									[textOnly]="true"
									iconName="download"
									[disabled]="
										!!regionImpact?.hasError ||
										!!regionImpact?.data?.[0]?.hasError
									"
									(handleClick)="
										onExportChart(
											worstRegionsBarChart.instance,
											'top_10_total_co2e_by_region'
										)
									"
								/>
								<globalictcc-button
									label="Export per device"
									[textOnly]="true"
									iconName="download"
									[disabled]="
										!!regionImpact?.hasError ||
										!!regionImpact?.data?.[1]?.hasError
									"
									(handleClick)="
										onExportChart(
											worstRegionsPerDeviceBarChart.instance,
											'avg_co2e_per_region'
										)
									"
								/>
							</div>
						</div>

						<div class="chart-grid">
							<div class="chart-wrapper">
								<span class="chart-label">Total</span>
								<globalictcc-chart
									#worstRegionsBarChart
									chartType="barY"
									[data]="regionImpact?.data?.[0]?.chart"
									[isLoading]="regionImpact?.isLoading ?? true"
									[hasError]="
										regionImpact?.hasError || regionImpact?.data?.[0]?.hasError
									"
									[isGradient]="true"
									[isTitleVisible]="false"
								/>
							</div>
							<div class="chart-wrapper">
								<span class="chart-label">Per device</span>
								<globalictcc-chart
									#worstRegionsPerDeviceBarChart
									chartType="barY"
									[data]="regionImpact?.data?.[1]?.chart"
									[isLoading]="regionImpact?.isLoading ?? true"
									[hasError]="
										regionImpact?.hasError || regionImpact?.data?.[1]?.hasError
									"
									[isGradient]="true"
									[isTitleVisible]="false"
								/>
							</div>
						</div>
					</section>
				} @placeholder {
					<div></div>
				}
			}

			<!-- Total Co2e Distribution by Install status -->
			@if (canSeeInstallStatus) {
				@let installStatus = installStatus$ | async;
				@defer (on viewport; prefetch on idle) {
					<section class="chart">
						<div class="section-title">
							<span class="chart-title">
								{{ installStatus?.data?.chart?.title }}
								<span class="icon-warning"></span>
							</span>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										co2ByInstallStatusPieChart.instance,
										'total_co2e_distribution_by_install_status'
									)
								"
							/>
						</div>

						<div class="table-chart">
							<globalictcc-chart
								#co2ByInstallStatusPieChart
								chartType="pie"
								height="471px"
								labelPosition="outside"
								[isTitleVisible]="false"
								[data]="installStatus?.data?.chart"
								[hasError]="
									installStatus?.hasError || installStatus?.data?.hasError
								"
								[isLoading]="installStatus?.isLoading ?? true"
								[colors]="co2ByInstallStatusPieChartColors"
								(handleChartRefresh)="refreshInstallStatus()"
							/>
						</div>
					</section>
				} @placeholder {
					<div></div>
				}
			}

			<!-- Saved Lifecycle Emissions -->
			@if (canSeeLifecycle) {
				@let lifecycleEmissions = lifecycleEmissions$ | async;
				@defer (on viewport; prefetch on idle) {
					<section class="chart">
						<div class="section-title">
							<span class="chart-title">
								{{ lifecycleEmissions?.data?.chart?.title }}
								<span class="icon-warning"></span>
							</span>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										savedLifecycleEmissionsLineChart.instance,
										'saved_lifecycle_emissions'
									)
								"
							/>
						</div>

						<div class="table-chart">
							<globalictcc-chart
								#savedLifecycleEmissionsLineChart
								chartType="line"
								[data]="lifecycleEmissions?.data?.chart"
								[isLoading]="lifecycleEmissions?.isLoading ?? true"
								[hasError]="
									lifecycleEmissions?.hasError ||
									lifecycleEmissions?.data?.hasError
								"
								[isLegendVisible]="true"
								[isTitleVisible]="false"
							/>
						</div>
					</section>
				} @placeholder {
					<div></div>
				}
			}

			<!-- Emission by country over time -->
			@if (canSeeCountryOverTime) {
				@let emissionsByCountry = emissionsByCountry$ | async;
				@defer (on viewport; prefetch on idle) {
					<section class="chart">
						<div class="section-title">
							<span class="chart-title">
								{{ emissionsByCountry?.data?.chart?.title }}
								<span class="icon-warning"></span>
							</span>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										emissionsByCountryOverTimeLineChart.instance,
										'emissions_by_country_over_time'
									)
								"
							/>
						</div>

						<div class="table-chart">
							<globalictcc-chart
								#emissionsByCountryOverTimeLineChart
								chartType="line"
								[data]="emissionsByCountry?.data?.chart"
								[isLoading]="emissionsByCountry?.isLoading ?? true"
								[hasError]="
									emissionsByCountry?.hasError ||
									emissionsByCountry?.data?.hasError
								"
								[isLegendVisible]="true"
								[isTitleVisible]="false"
								[configOverrides]="emissionsByCountryOverTimeChartOverrides"
							/>
						</div>
					</section>
				} @placeholder {
					<div></div>
				}
			}

			<div class="bottom-actions">
				<div class="actions">
					<globalictcc-button
						label="Download as CSV"
						iconName="download"
						[textOnly]="true"
						(handleClick)="onDownloadCsv()"
					/>
				</div>
			</div>
		} @else {
			<div class="title">
				<span class="header">Device detail report</span>

				<span class="sub-header">
					Upload your device list in excel format. Use the template as a reference to
					correctly visualize the data.
				</span>
			</div>

			<globalictcc-button
				[textOnly]="true"
				label="Download template"
				iconName="download"
				(handleClick)="onDownloadTemplate()"
			/>

			@if (canUpload) {
				<globalictcc-upload-file
					[errorMessage]="sameNameErrorMessage()"
					[acceptedFilesExtensions]="['.xlsx']"
					(handleUpload)="onFileUpload($event)"
					(handleRemove)="onRemoveFile()"
				/>

				<globalictcc-button
					label="Confirm"
					[disabled]="!uploadedFile() || isUploadDisabled()"
					(handleClick)="onConfirm()"
				/>
			} @else {
				<span class="sub-header">No device reports are available.</span>
			}
		}
		@if (isLoading) {
			<div
				class="loading"
				role="status"
				aria-live="polite"
			>
				<globalictcc-spinner />
				<span class="message">Loading...</span>
			</div>
		}
	</div>
</section>

<!-- Dialogs content -->
<ng-template #uploadDeviceList>
	<div class="upload-device-list-dialog-content">
		<span>
			Upload your device list in excel format. Use the template as a reference to correctly
			visualize the data.
		</span>

		<globalictcc-button
			[textOnly]="true"
			label="Download template"
			iconName="download"
			(handleClick)="onDownloadTemplate()"
		/>

		<globalictcc-upload-file
			[errorMessage]="sameNameErrorMessage()"
			[fluid]="true"
			[acceptedFilesExtensions]="['.xlsx']"
			(handleUpload)="onFileUpload($event)"
		/>
	</div>
</ng-template>

<ng-template #rename>
	<div class="rename-input-container">
		<globalictcc-input
			[formControl]="renameFileControl"
			[label]="uploadedFile()?.name || ''"
			[customErrorMessage]="sameNameErrorMessage()"
			(handleInputChange)="onRenameChange($event)"
		/>
	</div>
</ng-template>

<ng-template #export>
	<form
		class="export-container"
		[formGroup]="exportForm"
	>
		<span class="section-title">Include in report</span>
		<globalictcc-checkbox formControlName="generalInformation">
			General information
		</globalictcc-checkbox>
		<globalictcc-checkbox formControlName="manufacturerList">
			Manufacturer list
		</globalictcc-checkbox>
		<globalictcc-checkbox formControlName="manufacturerPieChart">
			Manufacturer pie chart
		</globalictcc-checkbox>

		<span class="section-title">Carbon footprint by device category</span>
		<globalictcc-checkbox formControlName="allCategory"> All categories </globalictcc-checkbox>
		<div class="radio-group">
			<globalictcc-checkbox formControlName="percentageView">
				Percentage view
			</globalictcc-checkbox>
			<globalictcc-checkbox formControlName="trueValueView">
				True value view
			</globalictcc-checkbox>
		</div>
	</form>
</ng-template>
